import React, { useState, useEffect, useMemo, useRef } from "react";
import { X, Search, Check, ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import { getData } from "~/functions/gridFunctions";

function buildHierarchy(accounts) {
  const map = {};
  const roots = [];
  accounts.forEach(a => { map[a.code] = { ...a, children: [] }; });
  accounts.forEach(a => {
    const parts = a.code.split(".");
    if (parts.length > 1) {
      parts.pop();
      const parent = parts.join(".");
      if (map[parent]) map[parent].children.push(map[a.code]);
      else roots.push(map[a.code]);
    } else roots.push(map[a.code]);
  });
  return roots;
}

function getAllChildren(account, allAccounts) {
  const accountMap = {};
  allAccounts.forEach(acc => { accountMap[acc.code] = { ...acc, children: [] }; });
  allAccounts.forEach(acc => {
    const parts = acc.code.split(".");
    if (parts.length > 1) {
      parts.pop();
      const parent = parts.join(".");
      if (accountMap[parent]) accountMap[parent].children.push(accountMap[acc.code]);
    }
  });
  function collect(node) {
    let res = [node];
    if (node.children?.length) node.children.forEach(c => { res = res.concat(collect(c)); });
    return res;
  }
  return accountMap[account.code] ? collect(accountMap[account.code]) : [account];
}

function HierarchyRow({ account, onRemove, level = 0 }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = account.children && account.children.length > 0;
  return (
    <>
      <li className="flex items-center justify-between px-2 py-1 text-xs border-t hover:bg-gray-50">
        <div style={{ marginLeft: `${level * 20}px` }} className="flex items-center gap-1 flex-1">
          {hasChildren ? (
            <button type="button" onClick={() => setExpanded(!expanded)} className="text-gray-500 hover:text-gray-700">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : <span className="w-3.5" />}
          <span>{account.code} - {account.name}</span>
        </div>
        <button type="button" onClick={() => onRemove(account.id)} className="px-2 py-1 text-white bg-red-600 hover:bg-red-700 rounded-sm ml-2" title="Remove">
          X
        </button>
      </li>
      {expanded && hasChildren && (
        <ul>
          {account.children.map(child => (
            <HierarchyRow key={child.id} account={child} onRemove={onRemove} level={level + 1} />
          ))}
        </ul>
      )}
    </>
  );
}

export default function ChartOfAccountsModal({ isOpen, onClose, selectedAccounts, onApply }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef();
  const filterPrefix = "02";

  useEffect(() => {
    if (isOpen) {
      loadAccounts();
      setSelected(selectedAccounts);
    }
  }, [isOpen, selectedAccounts]);

  async function loadAccounts() {
    setLoading(true);
    try {
      const data = await getData("chartofaccounts", {
        limit: 1000,
        page: 1,
        search: null,
        setPages: () => null,
        setGridDetails: () => null,
        defaultOrderBy: { column: null, asc: true },
      });
      const loadedData = Array.isArray(data) ? data : [];
      setAccounts(loadedData);
    } catch (error) {
      console.error("Error loading accounts:", error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }

  const prefixList = useMemo(
    () => accounts.filter(o => (o.code || "").startsWith(filterPrefix)),
    [accounts, filterPrefix]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return prefixList;
    return prefixList.filter(
      o =>
        o.code.toLowerCase().includes(q) ||
        o.name.toLowerCase().includes(q) ||
        (o.type && o.type.toLowerCase().includes(q)) ||
        `${o.code} - ${o.name}`.toLowerCase().includes(q)
    );
  }, [prefixList, query]);

  const selectedHierarchy = useMemo(() => buildHierarchy(selected), [selected]);

  function toggle(item) {
    const allChildren = getAllChildren(item, accounts);
    const allSelected = allChildren.every(child => selected.some(s => s.id === child.id));
    let next;
    if (allSelected) {
      next = selected.filter(s => !allChildren.some(child => child.id === s.id));
    } else {
      const toAdd = allChildren.filter(child => !selected.some(s => s.id === child.id));
      next = [...selected, ...toAdd];
    }
    setSelected(next);
  }

  function remove(id) {
    const accountToRemove = selected.find(s => s.id === id);
    if (!accountToRemove) return;

    const allToRemove = getAllChildren(accountToRemove, selected);
    const idsToRemove = allToRemove.map(acc => acc.id);

    const next = selected.filter(s => !idsToRemove.includes(s.id));
    setSelected(next);
  }

  function handleApply() {
    onApply(selected);
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex-1">
            <h2 className="text-xl font-bold">Chart of Accounts Filter</h2>
            <p className="text-sm text-gray-600 mt-1">
              Select Chart of Accounts starting with prefix "{filterPrefix}"
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2 bg-white border rounded-sm p-2">
            <Search size={16} className="text-gray-500" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search by code or name..."
              className="flex-1 outline-hidden text-sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Available Accounts (Prefix: {filterPrefix})</div>
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-gray-600 py-8">
                <Loader2 className="animate-spin" size={20} />
                <span>Loading accounts...</span>
              </div>
            ) : (
              <div className="max-h-96 overflow-auto border rounded-sm">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr>
                      <th className="text-left p-2 w-10">Sel</th>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td className="p-2 text-xs text-gray-500" colSpan={3}>No accounts found</td></tr>
                    )}
                    {filtered.map((opt) => {
                      const on = selected.some(s => s.id === opt.id);
                      return (
                        <tr key={opt.id} className="hover:bg-gray-50">
                          <td className="p-2">
                            <button
                              type="button"
                              onClick={() => toggle(opt)}
                              className={`w-6 h-6 flex items-center justify-center border rounded-sm ${on ? "bg-orange-500 text-white" : ""}`}
                              title={on ? "Remove" : "Add"}
                            >
                              {on ? <Check size={14} /> : "+"}
                            </button>
                          </td>
                          <td className="p-2">{opt.code}</td>
                          <td className="p-2">{opt.name}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="text-sm font-medium mb-2">Selected Accounts ({selected.length})</div>
            {selected.length === 0 ? (
              <div className="text-xs text-gray-400 p-4 border rounded-sm">No accounts selected</div>
            ) : (
              Object.entries(
                selected.reduce((acc, item) => {
                  const key = item.type || "Others";
                  acc[key] = acc[key] || [];
                  acc[key].push(item);
                  return acc;
                }, {})
              ).map(([group, items]) => {
                const groupHierarchy = buildHierarchy(items);
                return (
                  <div key={group} className="border rounded-sm mb-2">
                    <div className="px-2 py-1 text-xs font-semibold bg-gray-100">{group}</div>
                    <ul>
                      {groupHierarchy.map((account) => (
                        <HierarchyRow key={account.id} account={account} onRemove={remove} level={0} />
                      ))}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Apply ({selected.length})
          </button>
        </div>
      </div>
    </div>
  );
}
